import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { useClub } from '../hooks/useClub';

// ─── Constants ────────────────────────────────────────────────────────────────

const SESSION_TYPES = [
  'Open Play', 'Clinic', 'Lesson', 'Tournament',
  'Competitive Event', 'Reservation', 'Other'
];

const SESSION_ICONS = {
  'Open Play': '🏓', 'Clinic': '📋', 'Lesson': '🎓',
  'Tournament': '🏆', 'Competitive Event': '⚡',
  'Reservation': '📅', 'Other': '•', 'PbP': '🔄', 'Manual': '✋'
};

const ROLE_LABELS = { admin: 'ADMIN', mloc: 'SALES', mgr: 'MGR', fd: 'F.DESK' };

const INTERVENTION_TYPES_SIDEBAR = [
  'Booked Session',
  'Scheduled with Specific Group',
  'Manager Introduction',
  'Skill/Program Recommendation',
  'Explained Trial',
  'At Risk — Text/Call',
  'Resolve Issue',
  'Other',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name) {
  if (!name || name.trim() === '') return '??';
  return name.trim().split(/\s+/).map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function avatarColor(name) {
  const colors = ['#7c3aed', '#2563eb', '#059669', '#d97706', '#dc2626', '#0891b2', '#be185d'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0xffff;
  return colors[hash % colors.length];
}

function getTrialDay(trialStartDate) {
  if (!trialStartDate) return 0;
  const start = new Date(trialStartDate);
  start.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((today - start) / 86400000) + 1;
}

function getDayColor(day) {
  if (day >= 12) return '#ef4444';
  if (day >= 9)  return '#f97316';
  if (day >= 5)  return '#eab308';
  if (day >= 3)  return '#3b82f6';
  return '#6b7280';
}

function getMemberName(trial) {
  if (trial.full_name && trial.full_name.trim()) return trial.full_name.trim();
  if (trial.first_name) return `${trial.first_name} ${trial.last_initial || ''}`.trim();
  return 'Unknown';
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ name, size = 38 }) {
  const bg = avatarColor(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.36, color: '#fff',
      flexShrink: 0, fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.02em',
    }}>
      {getInitials(name)}
    </div>
  );
}

// ─── Visit Source Badge ───────────────────────────────────────────────────────

function SourceBadge({ source }) {
  const configs = {
    'PbP':        { color: '#22c55e', label: 'PbP' },
    'Manual':     { color: '#3b82f6', label: 'Manual' },
    'PbP+Manual': { color: '#8b5cf6', label: 'PbP+Manual' },
  };
  const cfg = configs[source] || { color: '#6b7280', label: source };
  return (
    <span style={{
      background: cfg.color + '22', color: cfg.color,
      border: `1px solid ${cfg.color}`, borderRadius: 4,
      padding: '1px 5px', fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
    }}>
      {cfg.label}
    </span>
  );
}

// ─── Visit Picker (inline session picker) ─────────────────────────────────────

function VisitPicker({ trial, onLog, onCancel }) {
  const [sessionType, setSessionType] = useState('Open Play');
  const [saving, setSaving] = useState(false);
  const name = getMemberName(trial);

  const handleLog = async () => {
    setSaving(true);
    await onLog(trial.member_id, sessionType);
    setSaving(false);
  };

  return (
    <div style={{
      background: '#0a0a0a', border: '1px solid #333', borderRadius: 8,
      padding: '12px 14px', marginTop: 8,
    }}>
      <div style={{ fontSize: 12, color: '#aaa', marginBottom: 8, fontFamily: 'DM Sans, sans-serif' }}>
        Log visit for <strong style={{ color: '#e5e5e5' }}>{name}</strong>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {SESSION_TYPES.map(type => (
          <button
            key={type}
            onClick={() => setSessionType(type)}
            style={{
              background: sessionType === type ? '#facc15' : '#1a1a1a',
              color: sessionType === type ? '#000' : '#aaa',
              border: `1px solid ${sessionType === type ? '#facc15' : '#333'}`,
              borderRadius: 6, padding: '5px 10px', fontSize: 11,
              cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: sessionType === type ? 700 : 400,
            }}
          >
            {SESSION_ICONS[type]} {type}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={handleLog}
          disabled={saving}
          style={{
            background: '#22c55e', color: '#000', border: 'none', borderRadius: 6,
            padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          {saving ? 'Logging…' : '✓ Log Visit'}
        </button>
        <button
          onClick={onCancel}
          style={{
            background: 'transparent', color: '#666', border: '1px solid #333',
            borderRadius: 6, padding: '7px 12px', fontSize: 12, cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Trial Card ───────────────────────────────────────────────────────────────

function TrialCard({ trial, onLogVisit, locationId, visitsLoggedToday }) {
  const [expanded, setExpanded] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showIntervention, setShowIntervention] = useState(false);
  const [interventionType, setInterventionType] = useState('');
  const [interventionNote, setInterventionNote] = useState('');
  const [interventionSaving, setInterventionSaving] = useState(false);
  const [interventionDone, setInterventionDone] = useState(false);
  const [visits, setVisits] = useState(null);
  const { get, put } = useApi();

  const name = getMemberName(trial);
  const day = getTrialDay(trial.trial_start_date) || parseInt(trial.day_number) || 0;
  const visitCount = parseInt(trial.visit_count) || parseInt(trial.total_visits) || 0;
  const visits30d   = parseInt(trial.visits_30d) || 0;
  const isPastMember = trial.is_past_member === true || trial.is_past_member === 'true';
  const isAtRisk = trial.at_risk === true || trial.at_risk === 'true';
  const isZeroVisits = visitCount === 0;
  const todayCount = visitsLoggedToday[trial.member_id] || 0;
  const dayColor = getDayColor(day);

  // Determine visit source label
  const hasPbP = trial.has_pbp_visits;
  const hasManual = trial.has_manual_visits;
  const sourceLabel = hasPbP && hasManual ? 'PbP+Manual' : hasPbP ? 'PbP' : hasManual ? 'Manual' : null;

  const loadVisits = async () => {
    if (visits !== null) return;
    try {
      const data = await get(`/t2m/checkin/visits/${trial.member_id}`);
      setVisits(Array.isArray(data) ? data : []);
    } catch (e) { setVisits([]); }
  };

  const handleExpand = () => {
    if (!expanded) loadVisits();
    setExpanded(e => !e);
  };

  const handleLog = async (memberId, sessionType) => {
    await onLogVisit(memberId, locationId, sessionType);
    setShowPicker(false);
    // Refresh visit history if expanded
    try {
      const data = await get(`/t2m/checkin/visits/${memberId}`);
      setVisits(Array.isArray(data) ? data : []);
    } catch (e) {}
  };

  const handleLogIntervention = async () => {
    if (!interventionType) return;
    setInterventionSaving(true);
    try {
      await put(`/t2m/trial/${trial.trial_id}`, {
        intervention_made: true,
        intervention_type: interventionType,
        intervention_by:   'staff',
        intervention_date: new Date().toISOString().split('T')[0],
        notes: interventionNote || undefined,
      });
      setInterventionDone(true);
      setShowIntervention(false);
      setInterventionType('');
      setInterventionNote('');
    } catch (e) {
      console.error('Intervention log failed', e);
    } finally {
      setInterventionSaving(false);
    }
  };

  return (
    <div style={{
      background: '#0d0d0d',
      border: `1px solid ${isAtRisk ? '#ef4444' : '#222'}`,
      borderRadius: 8,
      marginBottom: 8,
      overflow: 'hidden',
      boxShadow: isAtRisk ? '0 0 0 1px #ef444430' : 'none',
    }}>
      {/* Main card row */}
      <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>

        {/* Avatar */}
        <Avatar name={name} size={38} />

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={handleExpand}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#e5e5e5', fontFamily: 'DM Sans, sans-serif' }}>
              {name}
            </span>
            {isAtRisk && (
              <span style={{ background: '#ef444422', color: '#ef4444', border: '1px solid #ef4444', borderRadius: 4, padding: '1px 5px', fontSize: 9, fontWeight: 700 }}>
                AT RISK
              </span>
            )}
            {trial.id_verify_status === 'pending' && (
              <span style={{ background: '#eab30822', color: '#eab308', border: '1px solid #eab308', borderRadius: 4, padding: '1px 5px', fontSize: 9, fontWeight: 700 }}>
                VERIFY
              </span>
            )}
            {todayCount > 0 && (
              <span style={{ background: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e', borderRadius: 4, padding: '1px 5px', fontSize: 9, fontWeight: 700 }}>
                +{todayCount} TODAY
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
            {/* Day badge */}
            <span style={{
              background: dayColor + '22', color: dayColor,
              border: `1px solid ${dayColor}`, borderRadius: 4,
              padding: '1px 6px', fontSize: 10, fontWeight: 700, fontFamily: 'DM Mono, monospace',
            }}>
              Day {day}
            </span>
            {/* Visit count */}
            <span style={{
              background: isZeroVisits ? '#ef444422' : visitCount >= 3 ? '#22c55e22' : '#eab30822',
              color: isZeroVisits ? '#ef4444' : visitCount >= 3 ? '#22c55e' : '#eab308',
              border: `1px solid ${isZeroVisits ? '#ef4444' : visitCount >= 3 ? '#22c55e' : '#eab308'}`,
              borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700,
            }}>
              {visitCount} visit{visitCount !== 1 ? 's' : ''}
            </span>
            {trial.skill_level && trial.skill_level !== 'Unknown' && (
              <span style={{ fontSize: 10, color: '#666' }}>{trial.skill_level}</span>
            )}
            {sourceLabel && <SourceBadge source={sourceLabel} />}
            {/* Visits last 30 days pill */}
            <span style={{
              background: visits30d >= 4 ? '#22c55e22' : visits30d >= 2 ? '#eab30822' : '#6b728022',
              color: visits30d >= 4 ? '#22c55e' : visits30d >= 2 ? '#eab308' : '#6b7280',
              border: `1px solid ${visits30d >= 4 ? '#22c55e' : visits30d >= 2 ? '#eab308' : '#6b7280'}`,
              borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700,
            }}>
              {visits30d}x / 30d
            </span>
          </div>
        </div>

        {/* Right side: visit count large + +VISIT button */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: isZeroVisits ? '#ef4444' : '#22c55e', fontFamily: 'DM Mono, monospace', lineHeight: 1 }}>
            {visitCount}
          </div>
          {isZeroVisits ? (
            <button
              onClick={() => setShowPicker(p => !p)}
              style={{
                background: '#ef4444', color: '#fff', border: 'none',
                borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
              }}
            >
              +1ST VISIT
            </button>
          ) : (
            <button
              onClick={() => setShowPicker(p => !p)}
              style={{
                background: '#22c55e', color: '#000', border: 'none',
                borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
              }}
            >
              +VISIT
            </button>
          )}
        </div>
      </div>

      {/* Past member insight strip */}
      {isPastMember && (
        <div style={{
          margin: '0 12px 8px',
          padding: '5px 10px',
          background: 'rgba(59,130,246,0.08)',
          border: '1px solid rgba(59,130,246,0.25)',
          borderRadius: 6,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{ fontSize: 11 }}>💙</span>
          <span style={{ fontSize: 11, color: '#60a5fa', fontWeight: 600 }}>Past Member</span>
          {trial.membership_type && (
            <span style={{ fontSize: 10, color: '#3b82f6' }}>· {trial.membership_type}</span>
          )}
          <span style={{ fontSize: 10, color: '#3b82f6', marginLeft: 'auto' }}>Use win-back approach</span>
        </div>
      )}

      {/* Visit picker */}
      {showPicker && (
        <div style={{ padding: '0 12px 12px' }}>
          <VisitPicker trial={trial} onLog={handleLog} onCancel={() => setShowPicker(false)} />
        </div>
      )}

      {/* Expanded visit history */}
      {expanded && (
        <div style={{ padding: '0 12px 12px', borderTop: '1px solid #1a1a1a' }}>
          {visits === null && (
            <div style={{ padding: '8px 0', fontSize: 12, color: '#666' }}>Loading visit history…</div>
          )}
          {visits?.length === 0 && (
            <div style={{ padding: '8px 0', fontSize: 12, color: '#555' }}>No visits logged yet</div>
          )}
          {visits?.map(v => (
            <div key={v.visit_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #111' }}>
              <span style={{ fontSize: 14 }}>{SESSION_ICONS[v.session_type] || '•'}</span>
              <span style={{ fontSize: 12, color: '#bbb', flex: 1 }}>{v.session_type}</span>
              <span style={{ fontSize: 11, color: '#666' }}>{new Date(v.visit_date).toLocaleDateString()}</span>
              <SourceBadge source={v.source} />
            </div>
          ))}

          {/* Quick-log intervention — GAP-T9 */}
          <div style={{ marginTop: 10, borderTop: '1px solid #1a1a1a', paddingTop: 10 }}>
            {interventionDone ? (
              <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 700 }}>✓ Intervention logged</div>
            ) : !showIntervention ? (
              <button
                onClick={() => setShowIntervention(true)}
                style={{
                  background: 'transparent', color: '#f97316',
                  border: '1px solid #f9731640', borderRadius: 6,
                  padding: '5px 12px', fontSize: 11, fontWeight: 700,
                  cursor: 'pointer', width: '100%',
                }}
              >
                ⚡ Log Intervention
              </button>
            ) : (
              <div style={{ background: '#0d1117', border: '1px solid #f9731630', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: '#f97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  ⚡ Log Intervention
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                  {INTERVENTION_TYPES_SIDEBAR.map(t => (
                    <button
                      key={t}
                      onClick={() => setInterventionType(t)}
                      style={{
                        fontSize: 10, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
                        border: `1px solid ${interventionType === t ? '#f97316' : '#333'}`,
                        background: interventionType === t ? '#f9731620' : '#111',
                        color: interventionType === t ? '#f97316' : '#888',
                        fontWeight: interventionType === t ? 700 : 400,
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <textarea
                  value={interventionNote}
                  onChange={e => setInterventionNote(e.target.value)}
                  placeholder="Optional note..."
                  style={{
                    width: '100%', background: '#111', border: '1px solid #333',
                    color: '#ccc', borderRadius: 5, padding: '5px 7px', fontSize: 11,
                    resize: 'none', height: 40, fontFamily: 'inherit', boxSizing: 'border-box',
                    marginBottom: 8,
                  }}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={handleLogIntervention}
                    disabled={!interventionType || interventionSaving}
                    style={{
                      background: interventionType ? '#f97316' : '#333',
                      color: interventionType ? '#000' : '#666',
                      border: 'none', borderRadius: 6,
                      padding: '5px 14px', fontSize: 11, fontWeight: 700,
                      cursor: interventionType ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {interventionSaving ? 'Saving…' : '✓ Save'}
                  </button>
                  <button
                    onClick={() => { setShowIntervention(false); setInterventionType(''); setInterventionNote(''); }}
                    style={{
                      background: 'transparent', color: '#666', border: '1px solid #2a2a2a',
                      borderRadius: 6, padding: '5px 10px', fontSize: 11, cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PbP Sync Bar ─────────────────────────────────────────────────────────────

function PbpSyncBar({ lastUpload, manualSinceCount }) {
  if (!lastUpload) return null;
  const uploadTime = new Date(lastUpload).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const uploadDate = new Date(lastUpload).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const isToday = new Date(lastUpload).toDateString() === new Date().toDateString();

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: '#0a1a0a', border: '1px solid #1a3a1a',
      borderRadius: 6, padding: '6px 10px', margin: '8px 0', fontSize: 11,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', flexShrink: 0, boxShadow: '0 0 4px #22c55e' }} />
      <span style={{ color: '#22c55e', flex: 1 }}>
        PbP sync current — Last upload {isToday ? 'today' : uploadDate} at {uploadTime}
        {manualSinceCount > 0 && ` · ${manualSinceCount} manual entr${manualSinceCount !== 1 ? 'ies' : 'y'} since`}
      </span>
      <span style={{ background: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e33', borderRadius: 4, padding: '1px 5px', fontSize: 9, fontWeight: 700 }}>
        {manualSinceCount > 0 ? 'PbP+MANUAL' : 'PbP'}
      </span>
    </div>
  );
}

// ─── KPI Pills ────────────────────────────────────────────────────────────────

function KPIPills({ atRiskCount, loggedTodayCount, verifyPendingCount }) {
  return (
    <div style={{ display: 'flex', gap: 8, margin: '8px 0' }}>
      <div style={{ flex: 1, background: '#1a0a0a', border: '1px solid #ef444433', borderRadius: 6, padding: '6px 10px', textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#ef4444', fontFamily: 'DM Mono, monospace' }}>{atRiskCount}</div>
        <div style={{ fontSize: 9, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.05em' }}>At Risk</div>
      </div>
      <div style={{ flex: 1, background: '#0a1a0a', border: '1px solid #22c55e33', borderRadius: 6, padding: '6px 10px', textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#22c55e', fontFamily: 'DM Mono, monospace' }}>{loggedTodayCount}</div>
        <div style={{ fontSize: 9, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Logged Today</div>
      </div>
      <div style={{ flex: 1, background: '#1a1a0a', border: '1px solid #eab30833', borderRadius: 6, padding: '6px 10px', textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#eab308', fontFamily: 'DM Mono, monospace' }}>{verifyPendingCount}</div>
        <div style={{ fontSize: 9, color: '#eab308', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Verify Pending</div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CheckInSidebar() {
  const [open, setOpen] = useState(false);
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [visitsLoggedToday, setVisitsLoggedToday] = useState({});
  const [activeRoleTab, setActiveRoleTab] = useState(null); // null = show all
  const { get, post } = useApi();
  const { role } = useAuth();
  const { currentClub } = useClub();

  const clubName = currentClub?.name || 'Club';

  const loadTrials = useCallback(async () => {
    if (!currentClub?.id) return;
    setLoading(true);
    try {
      const data = await get(`/t2m/checkin/trials?clubId=${currentClub.id}`);
      setTrials(Array.isArray(data) ? data : []);
    } catch (e) {
      setTrials([]);
    } finally {
      setLoading(false);
    }
  }, [currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) loadTrials();
  }, [open, currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogVisit = async (memberId, locationId, sessionType) => {
    await post('/t2m/checkin/visit', {
      memberId,
      locationId,
      sessionType,
      visitDate: new Date().toISOString().split('T')[0],
    });
    setVisitsLoggedToday(prev => ({ ...prev, [memberId]: (prev[memberId] || 0) + 1 }));
    loadTrials();
  };

  // ── Filter ──────────────────────────────────────────────────────────────────
  const filtered = trials.filter(t => {
    if (!search) return true;
    const name = getMemberName(t).toLowerCase();
    const email = (t.email || '').toLowerCase();
    return name.includes(search.toLowerCase()) || email.includes(search.toLowerCase());
  });

  const atRisk = filtered.filter(t => t.at_risk === true || t.at_risk === 'true');
  const active = filtered.filter(t => t.at_risk !== true && t.at_risk !== 'true');

  // ── KPI counts ──────────────────────────────────────────────────────────────
  const atRiskCount = trials.filter(t => t.at_risk === true || t.at_risk === 'true').length;
  const loggedTodayCount = Object.values(visitsLoggedToday).reduce((a, b) => a + b, 0);
  const verifyPendingCount = trials.filter(t => t.id_verify_status === 'pending').length;

  // ── Role tabs (show current role highlighted, others clickable) ─────────────
  const roleTabs = ['fd', 'mgr', 'mloc', 'admin'];

  return (
    <>
      {/* ── Floating toggle button ── */}
      <button
        onClick={() => { setOpen(o => !o); }}
        title="Trial Check-In"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
          background: open ? '#333' : '#22c55e',
          color: open ? '#aaa' : '#000',
          border: 'none', borderRadius: 24,
          padding: '10px 18px', fontSize: 20,
          cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', gap: 6,
          fontFamily: 'DM Sans, sans-serif', fontWeight: 700,
          transition: 'all 0.15s',
        }}
      >
        🏓
        {!open && atRiskCount > 0 && (
          <span style={{
            background: '#ef4444', color: '#fff', borderRadius: 10,
            padding: '1px 6px', fontSize: 11, fontWeight: 700,
          }}>
            {atRiskCount}
          </span>
        )}
      </button>

      {/* ── Backdrop ── */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 998, background: 'rgba(0,0,0,0.4)' }}
        />
      )}

      {/* ── Sidebar panel ── */}
      {open && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 999,
          width: 400, maxWidth: '95vw',
          background: '#0a0a0a', borderLeft: '1px solid #222',
          display: 'flex', flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.6)',
          fontFamily: 'DM Sans, sans-serif',
        }}>

          {/* ── Header ── */}
          <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #1a1a1a', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#e5e5e5', letterSpacing: '0.04em' }}>
                  COMS · {clubName} · Trial Check-In
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Role tabs */}
                <div style={{ display: 'flex', gap: 4 }}>
                  {roleTabs.map(r => (
                    <button
                      key={r}
                      onClick={() => setActiveRoleTab(activeRoleTab === r ? null : r)}
                      style={{
                        background: (activeRoleTab === r || (activeRoleTab === null && r === role)) ? '#facc15' : 'transparent',
                        color: (activeRoleTab === r || (activeRoleTab === null && r === role)) ? '#000' : '#555',
                        border: `1px solid ${(activeRoleTab === r || (activeRoleTab === null && r === role)) ? '#facc15' : '#333'}`,
                        borderRadius: 4, padding: '3px 7px', fontSize: 10, fontWeight: 700,
                        cursor: 'pointer', letterSpacing: '0.04em',
                      }}
                    >
                      {ROLE_LABELS[r]}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setOpen(false)}
                  style={{ background: 'transparent', color: '#666', border: 'none', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 2 }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* KPI pills */}
            <KPIPills
              atRiskCount={atRiskCount}
              loggedTodayCount={loggedTodayCount}
              verifyPendingCount={verifyPendingCount}
            />

            {/* PbP sync bar */}
            <PbpSyncBar
              lastUpload={trials[0]?.last_upload_at || null}
              manualSinceCount={loggedTodayCount}
            />

            {/* Search */}
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              style={{
                width: '100%', background: '#111', color: '#e5e5e5',
                border: '1px solid #2a2a2a', borderRadius: 6,
                padding: '7px 12px', fontSize: 12, boxSizing: 'border-box',
                marginTop: 6,
              }}
            />

            {/* Instruction text */}
            <div style={{ fontSize: 10, color: '#555', marginTop: 6, letterSpacing: '0.02em' }}>
              At Risk first · Tap card to expand visit history · +VISIT logs immediately
            </div>
          </div>

          {/* ── List ── */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 24px' }}>

            {loading && (
              <div style={{ textAlign: 'center', padding: '32px 0', color: '#555', fontSize: 13 }}>
                Loading trials…
              </div>
            )}

            {!loading && trials.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 0', color: '#555', fontSize: 13 }}>
                No active trials for {clubName}
              </div>
            )}

            {/* AT RISK section */}
            {atRisk.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #ef444430',
                }}>
                  <span style={{ fontWeight: 700, fontSize: 11, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    AT RISK
                  </span>
                  <span style={{ background: '#ef4444', color: '#000', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>
                    {atRisk.length}
                  </span>
                </div>
                {atRisk.map(t => (
                  <TrialCard
                    key={t.trial_id}
                    trial={t}
                    onLogVisit={handleLogVisit}
                    locationId={currentClub?.id}
                    visitsLoggedToday={visitsLoggedToday}
                  />
                ))}
              </div>
            )}

            {/* ACTIVE TRIALS section */}
            {active.length > 0 && (
              <div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #22c55e30',
                }}>
                  <span style={{ fontWeight: 700, fontSize: 11, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    ACTIVE TRIALS
                  </span>
                  <span style={{ background: '#22c55e', color: '#000', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>
                    {active.length}
                  </span>
                </div>
                {active.map(t => (
                  <TrialCard
                    key={t.trial_id}
                    trial={t}
                    onLogVisit={handleLogVisit}
                    locationId={currentClub?.id}
                    visitsLoggedToday={visitsLoggedToday}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Footer refresh ── */}
          <div style={{ padding: '10px 16px', borderTop: '1px solid #1a1a1a', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#555' }}>{trials.length} active trial{trials.length !== 1 ? 's' : ''}</span>
            <button
              onClick={loadTrials}
              style={{ background: 'transparent', color: '#666', border: '1px solid #2a2a2a', borderRadius: 6, padding: '4px 12px', fontSize: 11, cursor: 'pointer' }}
            >
              ↻ Refresh
            </button>
          </div>
        </div>
      )}
    </>
  );
}
